import React, { useState, useMemo, useCallback, useEffect } from "react";
import { ChevronDown, ChevronRight, RefreshCw, Search, Calendar, Users, FileText, TrendingUp, DollarSign, Package, Loader2 } from "lucide-react";
import api from "../api/client";

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Helper functions
const getMonthYear = dateStr => {
  const date = new Date(dateStr);
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
};

const typeMultipliers = {
  "CashSale": -1,
  "CustInvc": -1,
  "CustCred": 1,
  "CashRefund": 1
};

const getMonthlyData = data => {
  const monthly = {};
  data.forEach(item => {
    const key = getMonthYear(item.Date);
    const multiplier = typeMultipliers[item.Type] ?? 0;
    const revenue = parseFloat(item["Net Amount"]) || 0;
    const quantity = parseFloat(item.Quantity) || 0;

    if (!monthly[key]) monthly[key] = { revenue: 0, quantity: 0 };
    monthly[key].revenue += revenue * multiplier;
    monthly[key].quantity += quantity * multiplier;
  });

  // Sort by date
  return Object.entries(monthly).sort((a, b) => {
    const dateA = new Date(a[0]);
    const dateB = new Date(b[0]);
    return dateA - dateB;
  });
};

const groupDataByCustomer = data => {
  const grouped = {};
  data.forEach(d => {
    const customer = d.Customer || "Unknown";
    const category = d.Category || "Unknown";
    const subCategory = d["Sub Category"] || "Unknown";

    grouped[customer] = grouped[customer] || {};
    grouped[customer][category] = grouped[customer][category] || {};
    grouped[customer][category][subCategory] = grouped[customer][category][subCategory] || [];
    grouped[customer][category][subCategory].push(d);
  });
  return grouped;
};

const sumRevenueQty = items =>
  items.reduce((acc, cur) => {
    const multiplier = typeMultipliers[cur.Type] ?? 0;
    const revenue = parseFloat(cur["Net Amount"]) || 0;
    const quantity = parseFloat(cur.Quantity) || 0;

    acc.total_revenue += revenue * multiplier;
    acc.total_quantity_sold += quantity * multiplier;
    return acc;
  }, { total_revenue: 0, total_quantity_sold: 0 });

const getItemSummary = data => {
  const map = {};
  data.forEach(item => {
    const multiplier = typeMultipliers[item.Type] ?? 0;
    const itemName = item.ITEM || "Unknown";
    const revenue = parseFloat(item["Net Amount"]) || 0;
    const quantity = parseFloat(item.Quantity) || 0;

    map[itemName] = map[itemName] || { revenue: 0, quantity: 0 };
    map[itemName].revenue += revenue * multiplier;
    map[itemName].quantity += quantity * multiplier;
  });
  return Object.entries(map)
    .map(([name, d]) => ({ item_name: name, total_revenue: d.revenue, total_quantity_sold: d.quantity }))
    .sort((a, b) => b.total_revenue - a.total_revenue);
};

const getSubCategoryItemSummary = items => {
  const map = {};
  items.forEach(item => {
    const multiplier = typeMultipliers[item.Type] ?? 0;
    const itemName = item.ITEM || "Unknown";
    const revenue = parseFloat(item["Net Amount"]) || 0;
    const quantity = parseFloat(item.Quantity) || 0;

    map[itemName] = map[itemName] || { total_revenue: 0, total_quantity_sold: 0 };
    map[itemName].total_revenue += revenue * multiplier;
    map[itemName].total_quantity_sold += quantity * multiplier;
  });
  return Object.entries(map).map(([item_name, d]) => ({
    item_name,
    total_revenue: d.total_revenue,
    total_quantity_sold: d.total_quantity_sold
  }));
};

const DashboardGrid = React.memo(({
  monthlyData,
  monthlyTotals,
  hierarchicalTotals,
  itemSummary,
  itemTotals,
  groupedData,
  openCustomers,
  openCategories,
  openSubCategories,
  toggleCustomer,
  toggleCategory,
  toggleSub
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 min-h-[calc(100vh-200px)]">
      {/* Monthly Statistics Cards */}
      <div className="lg:col-span-3 space-y-6">
        {/* Monthly Revenue Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Monthly Revenue</h2>
                <p className="text-emerald-100 text-sm">Track your revenue trends</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="overflow-y-auto max-h-80 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 rounded-lg">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700 rounded-tl-lg">Month</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-700 rounded-tr-lg">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {monthlyData.map(([m, d], i) => (
                    <tr key={m} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-3 font-medium text-gray-800">{m}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600">
                        ${d.revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-800">Total Revenue</span>
                <span className="text-xl font-bold text-emerald-600">
                  ${monthlyTotals.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Quantity Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Package className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Monthly Quantity</h2>
                <p className="text-blue-100 text-sm">Units sold over time</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="overflow-y-auto max-h-80 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 rounded-lg">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700 rounded-tl-lg">Month</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-700 rounded-tr-lg">Quantity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {monthlyData.map(([m, d], i) => (
                    <tr key={m} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-3 font-medium text-gray-800">{m}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-blue-600">{d.quantity.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-800">Total Quantity</span>
                <span className="text-xl font-bold text-blue-600">
                  {monthlyTotals.totalQuantity.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer & Category Analysis */}
      <div className="lg:col-span-5">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden h-full hover:shadow-xl transition-all duration-300">
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Sales by Customer & Category</h2>
                <p className="text-purple-100 text-sm">Hierarchical sales breakdown</p>
              </div>
            </div>
          </div>
          <div className="overflow-auto h-[calc(100%-80px)]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold text-gray-700">Customer / Category / Sub</th>
                  <th className="px-6 py-4 text-right font-semibold text-gray-700">Revenue</th>
                  <th className="px-6 py-4 text-right font-semibold text-gray-700">Qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {Object.entries(groupedData).map(([cust, cats]) => {
                  const allItems = Object.values(cats).flatMap(sub => Object.values(sub).flat());
                  const custSum = sumRevenueQty(allItems);
                  return (<React.Fragment key={cust}>
                    <tr
                      onClick={() => toggleCustomer(cust)}
                      className="cursor-pointer bg-gradient-to-r from-purple-50 to-purple-25 hover:from-purple-100 hover:to-purple-50 transition-all duration-200 border-l-4 border-purple-400"
                    >
                      <td className="px-6 py-4 flex items-center font-semibold text-gray-800">
                        <span className="mr-3 text-purple-600 transition-transform duration-200">
                          {openCustomers[cust] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </span>
                        {cust}
                      </td>
                      <td className="px-6 py-4 text-right font-mono font-semibold text-emerald-600">
                        ${custSum.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 text-right font-mono font-semibold text-gray-700">{custSum.total_quantity_sold.toLocaleString()}</td>
                    </tr>
                    {openCustomers[cust] && Object.entries(cats).map(([cat, subs]) => {
                      const catItems = Object.values(subs).flat();
                      const catSum = sumRevenueQty(catItems);
                      const catKey = `${cust}-${cat}`;
                      return (<React.Fragment key={cat}>
                        <tr
                          onClick={() => toggleCategory(cust, cat)}
                          className="cursor-pointer bg-gradient-to-r from-blue-50 to-blue-25 hover:from-blue-100 hover:to-blue-50 transition-all duration-200 border-l-4 border-blue-400"
                        >
                          <td className="px-6 py-3 pl-12 flex items-center font-medium text-gray-700">
                            <span className="mr-3 text-blue-600 transition-transform duration-200">
                              {openCategories[catKey] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </span>
                            {cat}
                          </td>
                          <td className="px-6 py-3 text-right font-mono font-medium text-emerald-600">
                            ${catSum.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-6 py-3 text-right font-mono font-medium text-gray-700">{catSum.total_quantity_sold.toLocaleString()}</td>
                        </tr>
                        {openCategories[catKey] && Object.entries(subs).map(([sub, items]) => {
                          const subSum = sumRevenueQty(items);
                          const subKey = `${cust}-${cat}-${sub}`;
                          const aggItems = getSubCategoryItemSummary(items);
                          return (<React.Fragment key={sub}>
                            <tr
                              onClick={() => toggleSub(cust, cat, sub)}
                              className="cursor-pointer bg-gradient-to-r from-amber-50 to-amber-25 hover:from-amber-100 hover:to-amber-50 transition-all duration-200 border-l-4 border-amber-400"
                            >
                              <td className="px-6 py-3 pl-16 flex items-center text-gray-700">
                                <span className="mr-3 text-amber-600 transition-transform duration-200">
                                  {openSubCategories[subKey] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                </span>
                                {sub}
                              </td>
                              <td className="px-6 py-3 text-right font-mono text-emerald-600">
                                ${subSum.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-6 py-3 text-right font-mono text-gray-600">{subSum.total_quantity_sold.toLocaleString()}</td>
                            </tr>
                            {openSubCategories[subKey] && aggItems.map((it, i) => (
                              <tr key={it.item_name + i} className="bg-white hover:bg-gray-50 transition-colors duration-150">
                                <td className="px-6 py-3 pl-20 text-gray-600">{it.item_name}</td>
                                <td className="px-6 py-3 text-right font-mono text-emerald-600">
                                  ${it.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                                <td className="px-6 py-3 text-right font-mono text-gray-600">{it.total_quantity_sold.toLocaleString()}</td>
                              </tr>
                            ))}
                          </React.Fragment>);
                        })}
                      </React.Fragment>);
                    })}
                  </React.Fragment>);
                })}
              </tbody>
            </table>
            <div className="sticky bottom-0 bg-gradient-to-r from-purple-100 to-purple-50 border-t-2 border-purple-300">
              <div className="px-6 py-4 flex justify-between items-center font-bold text-gray-800">
                <span>TOTAL</span>
                <div className="flex space-x-8">
                  <span className="text-purple-700">${hierarchicalTotals.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  <span className="text-purple-700">{hierarchicalTotals.totalQuantity.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Items Summary */}
      <div className="lg:col-span-4">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden h-full hover:shadow-xl transition-all duration-300">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Items Summary</h2>
                <p className="text-orange-100 text-sm">Top performing products</p>
              </div>
            </div>
          </div>
          <div className="overflow-auto h-[calc(100%-80px)]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold text-gray-700">Item Name</th>
                  <th className="px-6 py-4 text-right font-semibold text-gray-700">Revenue</th>
                  <th className="px-6 py-4 text-right font-semibold text-gray-700">Qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {itemSummary.map((it, i) => (
                  <tr key={it.item_name} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-3 font-medium text-gray-700 max-w-0 truncate" title={it.item_name}>
                      {it.item_name}
                    </td>
                    <td className="px-6 py-3 text-right font-mono font-medium text-emerald-600">
                      ${it.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-3 text-right font-mono font-medium text-gray-600">{it.total_quantity_sold.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="sticky bottom-0 bg-gradient-to-r from-orange-100 to-orange-50 border-t-2 border-orange-300">
              <div className="px-6 py-4 flex justify-between items-center font-bold text-gray-800">
                <span>TOTAL</span>
                <div className="flex space-x-8">
                  <span className="text-orange-700">${itemTotals.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  <span className="text-orange-700">{itemTotals.totalQuantity.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

function AnalyticsDashboard() {
  // API data state
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [startPicker, setStartPicker] = useState("2024-01-01");
  const [endPicker, setEndPicker] = useState("2024-12-31");
  const [repPicker, setRepPicker] = useState("ALL");
  const [typePicker, setTypePicker] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeStart, setActiveStart] = useState(startPicker);
  const [activeEnd, setActiveEnd] = useState(endPicker);
  const [activeRep, setActiveRep] = useState(repPicker);
  const [activeType, setActiveType] = useState(typePicker);
  const [activeSearchQuery, setActiveSearchQuery] = useState("");

  // Fetch data from API on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get('/api/getJsonFromS32024');
        const jsonData = response.data;

        const dataArray = Array.isArray(jsonData)
          ? jsonData
          : (jsonData.rows || []);

        setApiData(dataArray);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const applyFilter = () => {
    setActiveStart(startPicker);
    setActiveEnd(endPicker);
    setActiveRep(repPicker);
    setActiveType(typePicker);
    setActiveSearchQuery(searchQuery.trim().toLowerCase());
  };

  const salesReps = useMemo(() => {
    if (!apiData.length) return ["ALL"];
    return ["ALL", ...Array.from(new Set(apiData.map(i => i["Sales Rep"]).filter(Boolean)))];
  }, [apiData]);

  const saleTypes = useMemo(() => {
    if (!apiData.length) return ["ALL"];
    return ["ALL", ...Array.from(new Set(apiData.map(i => i.Type).filter(Boolean)))];
  }, [apiData]);

  const filteredData = useMemo(() => {
    if (!apiData.length) return [];

    const s = new Date(activeStart), e = new Date(activeEnd);
    const q = activeSearchQuery;

    return apiData.filter(item => {
      const d = new Date(item.Date);
      const inDate = d >= s && d <= e;
      const repMatch = activeRep === "ALL" || item["Sales Rep"] === activeRep;
      const typeMatch = activeType === "ALL" || item.Type === activeType;
      const matchesSearch = q === "" || (
        item.Customer?.toLowerCase().includes(q) ||
        item.Category?.toLowerCase().includes(q) ||
        item["Sub Category"]?.toLowerCase().includes(q) ||
        item.ITEM?.toLowerCase().includes(q)
      );
      return inDate && repMatch && typeMatch && matchesSearch;
    });
  }, [apiData, activeStart, activeEnd, activeRep, activeType, activeSearchQuery]);

  const monthlyData = useMemo(() => getMonthlyData(filteredData), [filteredData]);
  const groupedData = useMemo(() => groupDataByCustomer(filteredData), [filteredData]);
  const itemSummary = useMemo(() => getItemSummary(filteredData), [filteredData]);

  const monthlyTotals = useMemo(() => monthlyData.reduce((acc, [_, d]) => {
    acc.totalRevenue += d.revenue;
    acc.totalQuantity += d.quantity;
    return acc;
  }, { totalRevenue: 0, totalQuantity: 0 }), [monthlyData]);

  const hierarchicalTotals = useMemo(() => filteredData.reduce((acc, item) => {
    const multiplier = typeMultipliers[item.Type] ?? 0;
    const revenue = parseFloat(item["Net Amount"]) || 0;
    const quantity = parseFloat(item.Quantity) || 0;

    acc.totalRevenue += revenue * multiplier;
    acc.totalQuantity += quantity * multiplier;
    return acc;
  }, { totalRevenue: 0, totalQuantity: 0 }), [filteredData]);

  const itemTotals = useMemo(() => itemSummary.reduce((acc, item) => {
    acc.totalRevenue += item.total_revenue;
    acc.totalQuantity += item.total_quantity_sold;
    return acc;
  }, { totalRevenue: 0, totalQuantity: 0 }), [itemSummary]);

  const [openCustomers, setOpenCustomers] = useState({});
  const [openCategories, setOpenCategories] = useState({});
  const [openSubCategories, setOpenSubCategories] = useState({});

  const toggleCustomer = useCallback(c => setOpenCustomers(p => ({ ...p, [c]: !p[c] })), []);
  const toggleCategory = useCallback((c, cat) => setOpenCategories(p => ({ ...p, [`${c}-${cat}`]: !p[`${c}-${cat}`] })), []);
  const toggleSub = useCallback((c, cat, sub) => setOpenSubCategories(p => ({ ...p, [`${c}-${cat}-${sub}`]: !p[`${c}-${cat}-${sub}`] })), []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 text-indigo-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Data...</h2>
          <p className="text-gray-600">Fetching sales analytics from server</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-8 max-w-md">
          <div className="text-center">
            <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <span className="text-red-600 text-3xl">⚠</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Data</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="max-w-full mx-auto px-6 py-8">
        {/* Enhanced Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Sales Analytics</h1>
              <p className="text-gray-600 text-lg">Comprehensive sales performance dashboard</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Total Records</p>
                <p className="text-2xl font-bold text-indigo-600">{filteredData.length.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Enhanced Filter Bar */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex flex-wrap items-end gap-6">
              {/* Search Bar */}
              <div className="flex-1 min-w-80">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Search className="inline h-4 w-4 mr-2" />
                  Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search customers, categories, items..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
              </div>

              {/* Date Range */}
              <div className="flex space-x-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Calendar className="inline h-4 w-4 mr-2" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startPicker}
                    onChange={e => setStartPicker(e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={endPicker}
                    onChange={e => setEndPicker(e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                  />
                </div>
              </div>

              {/* Sales Rep */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Users className="inline h-4 w-4 mr-2" />
                  Sales Rep
                </label>
                <select
                  value={repPicker}
                  onChange={e => setRepPicker(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white min-w-32"
                >
                  {salesReps.map(rep => <option key={rep} value={rep}>{rep}</option>)}
                </select>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <FileText className="inline h-4 w-4 mr-2" />
                  Type
                </label>
                <select
                  value={typePicker}
                  onChange={e => setTypePicker(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white min-w-32"
                >
                  {saleTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              {/* Apply Button */}
              <button
                onClick={applyFilter}
                className="flex items-center space-x-2 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              >
                <RefreshCw size={18} />
                <span>Apply Filters</span>
              </button>
            </div>
          </div>
        </div>

        <DashboardGrid
          monthlyData={monthlyData}
          monthlyTotals={monthlyTotals}
          hierarchicalTotals={hierarchicalTotals}
          itemSummary={itemSummary}
          itemTotals={itemTotals}
          groupedData={groupedData}
          openCustomers={openCustomers}
          openCategories={openCategories}
          openSubCategories={openSubCategories}
          toggleCustomer={toggleCustomer}
          toggleCategory={toggleCategory}
          toggleSub={toggleSub}
        />
      </div>
    </div>
  );
}

export default AnalyticsDashboard;